import { Location, NgClass } from "@angular/common";
import { Component, computed, OnInit, signal, ViewChild } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute, Router } from "@angular/router";
import { ProductInputComponent } from "@components/product-input/product-input.component";
import {
  getControlsForGeneralCategory,
  isLivestockCategoryName,
  LIVESTOCK_SEGMENT,
  livestockInputControls,
  otherProductInputControls,
  TANK_SEGMENT,
  tankInputControls,
} from "@components/product-input/product-input-controls";
import { IApiResponse } from "@models/api-response.model";
import { IProductRequestDto } from "@models/product-list.model";
import { AppStateService, RequestService } from "@services/index";

interface ValidationError {
  path: string;
  message: string;
}

@Component({
  selector: "fh-product-input-page",
  standalone: true,
  imports: [ProductInputComponent, NgClass, MatIconModule],
  templateUrl: "./product-input-page.component.html",
  styleUrl: "./product-input-page.component.scss",
})
export class ProductInputPageComponent implements OnInit {
  readonly generalCategorySegments = [
    { label: "Livestock", id: 1, controls: livestockInputControls },
    { label: "Tank", id: 5, controls: tankInputControls },
    { label: "Others", id: 0, controls: otherProductInputControls },
  ];

  errors = signal<ValidationError[]>([]);
  success = signal<string>("");
  /**
   * A save in progress, until the form has reloaded with the new options' ids:
   * Save stays off meanwhile, as a second one would store those options again.
   */
  saving = signal<boolean>(false);

  productInput = {
    data: signal<IProductRequestDto>({} as IProductRequestDto),
    valid: signal<boolean>(false),
  };
  productId = signal<number>(-1);
  @ViewChild(ProductInputComponent) private productInputComponent?: ProductInputComponent;
  /** Null until the product loads, and for a product being created. */
  productScore = signal<number | null>(null);

  selectedCategorySegment = signal<number>(-1);

  /**
   * The main-category dropdown is locked when the chosen form leaves no choice.
   * Livestock now has more than one main category — Freshwater and Marine — so it
   * has to stay selectable; Tank still has exactly one.
   */
  isCategoryEditable = computed(() => {
    if (this.selectedCategorySegment() === TANK_SEGMENT) return false;
    if (this.selectedCategorySegment() !== LIVESTOCK_SEGMENT) return true;
    return (
      this.appStateService.parentCategories().filter((c) => isLivestockCategoryName(c.name))
        .length > 1
    );
  });

  formTitle = computed(
    () =>
      getControlsForGeneralCategory(
        this.selectedCategorySegment(),
        this.appStateService.parentCategories()
      ).title
  );

  constructor(
    readonly _router: Router,
    private readonly requestService: RequestService,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly appStateService: AppStateService
  ) {}

  ngOnInit() {
    const productId = parseInt(this.route.snapshot.paramMap.get("id") ?? "0", 10);
    this.productId.set(productId);
    this.selectedCategorySegment.set(productId ? -1 : 1);

    // Load parent categories into app state if not already loaded
    this.loadParentCategories();
  }

  private loadParentCategories(): void {
    // Only load if not already loaded
    if (this.appStateService.parentCategories().length === 0) {
      this.requestService.get("/api/v2/categories?parent_id=0&status=active").subscribe({
        next: (res: any) => {
          if (res.status === true) {
            // Map to include 'name' field for compatibility
            const categories = (res.data?.categories || []).map((cat: any) => ({
              ...cat,
              name: cat.category_name,
            }));
            this.appStateService.parentCategories.set(categories);
          }
        },
        error: (error) => {
          console.error("Error loading parent categories:", error);
        },
      });
    }
  }

  goBack(): void {
    this.location.back();
  }

  private clearErrors() {
    this.errors.set([]);
  }

  private handleError(error: string | ValidationError[]) {
    if (typeof error === "string") {
      this.errors.set([{ path: "general", message: error }]);
    } else {
      const formattedErrors = error.map((err) => ({
        path: err.path
          .split(".")
          .map((part) => part.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()))
          .join(" > "),
        message: err.message,
      }));
      this.errors.set(formattedErrors);
    }
    // Clear errors after 5 seconds
    setTimeout(() => {
      this.clearErrors();
    }, 5000);
  }

  saveProduct() {
    if (this.saving()) return;
    this.clearErrors();
    const data = this.productInput.data();

    const { images, videos, ...rest } = data;
    const requestBody = { ...rest } as any;

    requestBody.images = images.map((img) => {
      if (img.state === "add") {
        return img;
      }
      delete img.base64;
      return img;
    });

    requestBody.videos = videos
      .filter((video) => video.state === "delete")
      .map((video) => {
        delete video.base64;
        return video;
      });
    // Only now: a throw building the body above must not leave Save disabled.
    this.saving.set(true);

    const saveVideos = (productId: string) => {
      const fd = new FormData();
      const videosToUpload = videos.filter((video) => video.state === "add");

      return new Promise<boolean>((resolve) => {
        if (videosToUpload.length > 0) {
          videosToUpload.forEach((video) => {
            fd.append(`video_${Date.now()}`, video.file as unknown as File);
          });
          fd.append("productId", productId);

          // Resolves whatever happens: the form must still reload afterwards.
          this.requestService.put("/api/v2/product-videos", fd).subscribe({
            next: (res: any) => resolve(res?.status === true),
            error: () => resolve(false),
          });
        } else {
          resolve(true);
        }
      });
    };

    this.requestService
      .post<IApiResponse>(`/api/v2/product?productId=${this.productId()}`, data)
      .subscribe({
        next: (res) => {
          if (res.status) {
            saveVideos(res.data.id)
              .then((videosSaved) => {
                this.success.set(
                  videosSaved
                    ? "Record saved successfully."
                    : "Saved, but the video upload failed - try adding it again."
                );
                setTimeout(() => {
                  this.success.set("");
                }, 5000);

                // Stay on this page after saving rather than jumping to the product list.
                // A brand-new product (productId was 0) just got assigned a real id -- fix
                // the URL to match so a page refresh (or a second Save) edits it instead of
                // silently creating another product. replaceState avoids a real Angular
                // navigation, so the in-progress edit state here isn't touched.
                if (!this.productId() && res.data?.id) {
                  // The new id re-runs the form's load, which picks up saved ids:
                  // wait for that one load - a second could wipe newer input.
                  const loaded = this.productInputComponent?.whenLoaded(res.data.id);
                  this.productId.set(res.data.id);
                  this.location.replaceState(`/product_input/${res.data.id}`);
                  return loaded;
                }
                // Options added this session now exist: reload them with their
                // ids so another Save edits rather than adds them again - and
                // only then allow another Save.
                return this.productInputComponent?.reloadFromServer(res.data?.id);
              })
              .finally(() => this.saving.set(false));
          } else {
            // Handle validation errors
            const errorMessage = Array.isArray(res.data)
              ? res.data
              : res.message || "An error occurred while saving the product";
            this.handleError(errorMessage);
            this.saving.set(false);
          }
        },
        error: () => {
          this.saving.set(false);
          this.handleError("An unexpected error occurred. Please try again.");
        },
      });
  }

  onCategoryDetermined(categoryId: number) {
    // Resolved against the loaded categories so any livestock category — not just
    // id 1 — lands on the livestock form.
    const { generalCategorySegment } = getControlsForGeneralCategory(
      categoryId,
      this.appStateService.parentCategories()
    );
    this.selectedCategorySegment.set(generalCategorySegment);
  }
}
